import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Phone,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Check,
  Stethoscope,
  Heart,
  Pill,
  Users,
  ShieldAlert,
} from "lucide-react";

const KINDS = [
  { key: "doctor", label: "Doctor", Icon: Stethoscope, color: "text-clinical-primary" },
  { key: "family", label: "Family", Icon: Heart, color: "text-afyanex-pink" },
  { key: "pharmacy", label: "Pharmacy", Icon: Pill, color: "text-clinical-secondary" },
  { key: "emergency", label: "Emergency", Icon: ShieldAlert, color: "text-destructive" },
  { key: "other", label: "Other", Icon: Users, color: "text-clinical-subtext" },
];

const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[KINDS.length - 1];

const blank = { label: "", name: "", phone: "", kind: "other" };

export default function QuickContactsEditor({ clientId, emergencyNote }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get(`/clients/${clientId}/contacts`)
      .then(({ data }) => setContacts(data || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const openAdd = () => {
    setEditingId(null);
    setForm(blank);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ label: c.label || "", name: c.name || "", phone: c.phone || "", kind: c.kind || "other" });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
    setForm(blank);
  };

  const save = async () => {
    if (!form.name.trim() && !form.phone.trim()) {
      toast.error("Name or phone is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: (form.label || kindMeta(form.kind).label).trim(),
        name: form.name.trim(),
        phone: form.phone.trim(),
        kind: form.kind,
      };
      if (editingId) {
        await api.put(`/clients/${clientId}/contacts/${editingId}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post(`/clients/${clientId}/contacts`, payload);
        toast.success("Contact added");
      }
      await load();
      closeForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setSaving(true);
    try {
      await api.delete(`/clients/${clientId}/contacts/${id}`);
      toast.success("Contact removed");
      await load();
      setConfirmDeleteId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface p-4" data-testid="quick-contacts">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">Quick contacts</div>
        <button
          type="button"
          onClick={openAdd}
          className="btn-ghost text-clinical-primary"
          data-testid="add-contact-button"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* 911 — always present, never user-removable */}
        <a
          href="tel:911"
          className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10"
          data-testid="tap-call-911"
        >
          <ShieldAlert className="h-4 w-4" /> 911 · Emergency
        </a>

        {loading ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-clinical-border bg-clinical-bg px-3 py-2.5 text-xs text-clinical-subtext italic sm:col-span-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : contacts.length === 0 ? (
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-2 rounded-lg border border-dashed border-clinical-border bg-clinical-bg px-3 py-2.5 text-xs text-clinical-subtext hover:border-clinical-primary/40 hover:text-clinical-primary"
            data-testid="contacts-empty-cta"
          >
            <Plus className="h-3.5 w-3.5" /> Add a contact (doctor, family, pharmacy…)
          </button>
        ) : (
          contacts.map((c) => {
            const { Icon, color, label: kindLabel } = kindMeta(c.kind);
            const displayPhone = c.phone || "—";
            return (
              <div
                key={c.id}
                className="group relative flex items-center gap-2 rounded-lg border border-clinical-border bg-white pl-3 pr-2 py-2 text-sm text-clinical-text hover:border-clinical-primary/40 transition-colors"
                data-testid={`contact-row-${c.id}`}
              >
                <a
                  href={c.phone ? `tel:${c.phone.replace(/[^\d+]/g, "")}` : undefined}
                  onClick={(e) => {
                    if (!c.phone) e.preventDefault();
                  }}
                  className={`flex-1 flex items-center gap-2 min-w-0 ${
                    c.phone ? "" : "cursor-default pointer-events-none"
                  }`}
                  data-testid={`tap-call-${c.id}`}
                >
                  <Phone className={`h-4 w-4 ${color}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs text-clinical-subtext truncate">
                      {c.label || kindLabel}
                      {c.name && c.label && c.name !== c.label && ` · ${c.name}`}
                      {!c.label && c.name && ` · ${c.name}`}
                    </span>
                    <span className="block truncate font-medium">{displayPhone}</span>
                  </span>
                </a>
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="btn-ghost !p-1.5 opacity-70 hover:opacity-100"
                  data-testid={`edit-contact-${c.id}`}
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {confirmDeleteId === c.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      disabled={saving}
                      className="btn-ghost !p-1.5 !text-destructive"
                      data-testid={`confirm-delete-contact-${c.id}`}
                      title="Confirm delete"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="btn-ghost !p-1.5"
                      title="Cancel"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(c.id)}
                    className="btn-ghost !p-1.5 opacity-70 hover:opacity-100 hover:!text-destructive"
                    data-testid={`delete-contact-${c.id}`}
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {emergencyNote && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {emergencyNote}
        </div>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
          onClick={closeForm}
          data-testid="contact-form-modal"
        >
          <div
            className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
              <h3 className="font-heading text-lg font-medium">
                {editingId ? "Edit contact" : "Add contact"}
              </h3>
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="btn-ghost !p-1.5"
                data-testid="contact-form-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs text-clinical-subtext block mb-1.5">Kind</label>
                <div className="grid grid-cols-5 gap-1.5">
                  {KINDS.map((k) => {
                    const active = form.kind === k.key;
                    return (
                      <button
                        key={k.key}
                        type="button"
                        onClick={() => setForm({ ...form, kind: k.key })}
                        className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] transition-colors ${
                          active
                            ? "border-clinical-primary bg-clinical-primary/5 text-clinical-primary"
                            : "border-clinical-border text-clinical-subtext hover:border-clinical-primary/40"
                        }`}
                        data-testid={`contact-kind-${k.key}`}
                      >
                        <k.Icon className="h-4 w-4" />
                        {k.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">
                  Label <span className="text-clinical-subtext/70">(optional)</span>
                </label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder={kindMeta(form.kind).label}
                  className="input-clinical"
                  data-testid="contact-label-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dr. Smith / Lisa (daughter)"
                  className="input-clinical"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-clinical-subtext block mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="555-123-4567"
                  className="input-clinical"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-clinical-border">
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="btn-secondary"
                data-testid="contact-form-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="btn-primary"
                data-testid="contact-form-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />{" "}
                    {editingId ? "Saving…" : "Adding…"}
                  </>
                ) : editingId ? (
                  "Save changes"
                ) : (
                  "Add contact"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
